import React from 'react';
import { Loader2, Unplug } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DISCONNECT_COPY, type DisconnectIntegration } from './disconnectCopy';

interface DisconnectConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  isLoading?: boolean;
  integration?: DisconnectIntegration;
  name?: string;
  title?: string;
  description?: React.ReactNode;
  confirmLabel?: string;
}

export const DisconnectConfirmDialog: React.FC<DisconnectConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  integration,
  name,
  title,
  description,
  confirmLabel = 'Disconnect',
}) => {
  const catalog = integration ? DISCONNECT_COPY[integration] : undefined;
  const resolvedName = name ?? catalog?.name ?? 'this account';
  const resolvedTitle = title ?? `Disconnect ${resolvedName}?`;
  const resolvedDescription =
    description ??
    catalog?.description ??
    `This will disconnect ${resolvedName} from this business account. Related features will stop working until you reconnect. You can reconnect anytime.`;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="flex w-[calc(100%-32px)] sm:w-[min(100%,500px)] max-w-[500px] flex-col gap-[15px] rounded-[16px] border-0 bg-white p-5 shadow-[0_20px_60px_rgba(0,0,0,0.15)] sm:rounded-[16px]">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={isLoading}
          aria-label="Close"
          className="absolute right-5 top-5 bg-transparent p-0 text-[#667085] transition hover:text-[#101828] disabled:pointer-events-none disabled:opacity-40"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M8 8l8 8M16 8l-8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(239,68,68,0.08)]">
            <Unplug className="h-6 w-6 text-[#ef4444]" strokeWidth={2} />
          </div>
        </div>
        <AlertDialogHeader className="flex flex-col gap-2.5 space-y-0 text-center sm:text-center">
          <AlertDialogTitle className="text-center text-[18px] font-semibold leading-tight tracking-tight text-[#101828]">
            {resolvedTitle}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-center text-[13px] font-normal leading-[1.6] text-[#667085]">
              {resolvedDescription}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="border-t border-[#F2F4F7]" />
        <AlertDialogFooter className="flex-row gap-3 sm:justify-end sm:space-x-0">
          <AlertDialogCancel
            disabled={isLoading}
            className="mt-0 h-auto flex-1 sm:flex-none rounded-[8px] border border-[#e5e7eb] bg-white px-6 py-2.5 text-[14px] font-medium text-[#101828] shadow-none hover:bg-[#F9FAFB] hover:text-[#101828]"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              Promise.resolve(onConfirm()).catch(() => {});
            }}
            disabled={isLoading}
            className="h-auto flex-1 sm:flex-none rounded-[8px] border-0 bg-[#ef4444] px-[30px] py-2.5 text-[14px] font-medium text-white shadow-none hover:bg-[#ef4444] hover:text-white hover:opacity-90 disabled:opacity-60"
          >
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DisconnectConfirmDialog;
